import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ExternalLink, MessageSquare, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppUnavailable from "@/pages/AppUnavailable";

const DOT = { green: "#16a34a", yellow: "#f59e0b", red: "#dc2626" };
const FILTERS = [
  { key: "red,yellow", label: "Needs attention" },
  { key: "red", label: "Red" },
  { key: "yellow", label: "Yellow" },
  { key: "green", label: "Helpful" },
  { key: "", label: "All" },
];

export default function KBFeedbackDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = usePersistedState("kb-feedback.filter", "red,yellow");

  const load = useCallback(() => {
    setData(null);
    const resolved = filter === "red,yellow" ? "false" : "";
    api.get("/kb/feedback", { params: { rating: filter, resolved, limit: 400 } })
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); else setData({ feedback: [], tallies: {} }); });
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const toggleResolve = async (f, resolved) => {
    try {
      await api.post("/kb/feedback/resolve", { article_id: f.article_id, user_id: f.user_id, resolved });
      toast.success(resolved ? "Marked resolved" : "Reopened");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (denied) return <AppUnavailable appName="KB Feedback" inactive={false} />;

  const t = data?.tallies || {};
  return (
    <div className="kb-surface" data-testid="kb-feedback-page">
      <div className="mb-5 flex items-center gap-3">
        <button onClick={() => navigate("/kb")} className="text-slate-400 dark:text-slate-500 hover:text-primary" data-testid="kb-feedback-back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-slate-100">Article feedback</h1>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3">
        {["red", "yellow", "green"].map((r) => (
          <div key={r} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3" data-testid={`kb-feedback-tally-${r}`}>
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: DOT[r] }} />
              <span className="text-2xl font-bold text-slate-800 dark:text-slate-100">{t[r] ?? 0}</span>
            </div>
            <div className="text-xs capitalize text-slate-400 dark:text-slate-500">{r === "green" ? "helpful" : r}</div>
          </div>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`kb-feedback-filter-${f.key || "all"}`} onClick={() => setFilter(f.key)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === f.key ? "border-primary bg-primary/10 text-primary" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {!data ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" /></div>
      ) : data.feedback.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 py-16 text-center text-sm text-slate-400 dark:text-slate-500" data-testid="kb-feedback-empty">
          <MessageSquare className="mx-auto mb-2 h-6 w-6 text-slate-300 dark:text-slate-600" /> No feedback in this view yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-testid="kb-feedback-list">
          {data.feedback.map((f) => (
            <div key={f.article_id + f.user_id} data-testid={`kb-feedback-row-${f.article_id}`}
              className="flex items-start gap-3 border-b border-slate-100 dark:border-slate-800 px-4 py-3 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800">
              <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: DOT[f.rating] || "#94a3b8" }} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => navigate(`/kb/a/${f.slug}`)} className="truncate text-sm font-medium text-slate-800 dark:text-slate-100 hover:text-primary hover:underline">
                    {f.article_name || f.slug}
                  </button>
                  {f.resolved && <span className="rounded bg-emerald-100 px-1.5 text-[10px] font-medium text-emerald-700">Resolved</span>}
                  {f.notified && <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 text-[10px] text-slate-400 dark:text-slate-500">emailed</span>}
                </div>
                {f.comment && <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">&ldquo;{f.comment}&rdquo;</p>}
                <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                  {f.user_name} · {(f.updated_at || "").slice(0, 10)}
                  {f.resolved && f.resolved_by ? ` · resolved by ${f.resolved_by}` : ""}
                </p>
              </div>
              <div className="mt-0.5 flex shrink-0 items-center gap-1">
                {f.rating !== "green" && (
                  f.resolved ? (
                    <button onClick={() => toggleResolve(f, false)} data-testid={`kb-feedback-reopen-${f.article_id}`}
                      title="Reopen" className="flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-800 px-2 py-1 text-[11px] text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800">
                      <RotateCcw className="h-3 w-3" /> Reopen
                    </button>
                  ) : (
                    <button onClick={() => toggleResolve(f, true)} data-testid={`kb-feedback-resolve-${f.article_id}`}
                      title="Mark resolved" className="flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-50">
                      <Check className="h-3 w-3" /> Resolve
                    </button>
                  )
                )}
                <a href={`/kb/a/${f.slug}`} target="_blank" rel="noreferrer" className="text-slate-300 dark:text-slate-600 hover:text-primary" data-testid={`kb-feedback-open-${f.article_id}`}>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
